import {
  Modal,
  Stack,
  Box,
  Typography,
  IconButton,
  FormControl,
  Autocomplete,
  TextField,
  Button,
} from "@mui/material";
import {
  useState,
  SyntheticEvent,
  useCallback,
  FormEvent,
  useMemo,
} from "react";
import { desktopSx } from "src/utils/styles/modalStyles";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useGetRolesQuery } from "src/api/services/rolesApi";
import { useAssignRolesMutation } from "src/api/services/employeeApi";

interface Props {
  open: boolean;
  onClose: () => void;
  userId: string;
  currentRoles: string[];
}

const CreateUserRolesModal = ({
  open,
  onClose,
  currentRoles,
  userId,
}: Props) => {
  const { data: roles = [] } = useGetRolesQuery();
  const [addRoles, { isLoading }] = useAssignRolesMutation();
  const [assignedRoles, setAssignedRoles] = useState<string[]>(currentRoles);

  const assignableRoles = useMemo(() => {
    return roles.map((r) => r.name);
  }, [roles]);

  const handleChange = (
    _event: SyntheticEvent<Element, Event>,
    value: string[]
  ) => {
    setAssignedRoles(value);
  };

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      addRoles({
        user_id: userId,
        roles: roles
          .filter((r) => assignedRoles.includes(r.name))
          .map((r) => r.id),
      })
        .unwrap()
        .then(onClose);
    },
    [addRoles, assignedRoles, onClose, roles, userId]
  );

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Roles
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>

        <FormControl fullWidth>
          <Autocomplete
            onChange={handleChange}
            multiple
            value={assignedRoles}
            fullWidth
            disablePortal
            options={assignableRoles}
            renderInput={(params) => <TextField {...params} label="Roles" />}
          />
        </FormControl>

        <Stack direction="row" gap={2} sx={{ marginTop: "auto" }}>
          <Button fullWidth variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton fullWidth loading={isLoading} type="submit">
            Create
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default CreateUserRolesModal;
